import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import {
  ShieldExclamationIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  EyeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ServerIcon,
  GlobeAltIcon,
  CpuChipIcon,
  FireIcon,
  BugAntIcon,
  DocumentTextIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  BoltIcon,
  ShieldCheckIcon,
  ExclamationCircleIcon,
  BeakerIcon,
  CommandLineIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import toast from '../utils/notify';
import { DataTable } from './common/DataTable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AutomatedThreatResponse = () => {
  const { user, token } = useAuth();
  
  // State management
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [detections, setDetections] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [threatIntel, setThreatIntel] = useState([]);
  const [cases, setCases] = useState([]);
  const [agents, setAgents] = useState([]);
  const [mitreData, setMitreData] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    severity: '',
    source: '',
    status: '',
    mitreTactic: ''
  });
  
  // Modal states
  const [selectedDetection, setSelectedDetection] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [newCaseData, setNewCaseData] = useState({
    title: '',
    description: '',
    severity: 'medium',
    assigned_analyst: '',
    mitre_techniques: [],
    related_detections: [],
    iocs: [],
    impact: 'medium'
  });
  
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    fetchDashboardData();
    fetchDetections();
    fetchPlaybooks();
    fetchThreatIntel();
    fetchCases();
    fetchAgents();
    fetchMitreData();
    
    // Auto-refresh every 30 seconds
    const interval = autoRefresh ? setInterval(() => {
      fetchDashboardData();
      fetchDetections();
    }, 30000) : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [token, autoRefresh]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API}/virtual-soc/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch dashboard data');
      const data = await response.json();
      setDashboardData(data);
      setLoading(false);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const fetchDetections = async () => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.severity) queryParams.append('severity', filters.severity);
      if (filters.source) queryParams.append('source', filters.source);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.mitreTactic) queryParams.append('mitre_tactic', filters.mitreTactic);
      
      const response = await fetch(`${API}/virtual-soc/events/?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch detections');
      const data = await response.json();
      setDetections(data.items || []);
    } catch (error) {
      console.error('Detections fetch error:', error);
      toast.error('Failed to load threat detections');
    }
  };

  const fetchPlaybooks = async () => {
    try {
      const response = await fetch(`${API}/virtual-soc/playbooks/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch playbooks');
      const data = await response.json();
      setPlaybooks(data.items || []);
    } catch (error) {
      console.error('Playbooks fetch error:', error);
      toast.error('Failed to load response playbooks');
    }
  };

  const fetchThreatIntel = async () => {
    try {
      const response = await fetch(`${API}/virtual-soc/threat-intelligence/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch threat intelligence');
      const data = await response.json();
      setThreatIntel(data.indicators || []);
    } catch (error) {
      console.error('Threat intel fetch error:', error);
      toast.error('Failed to load threat intelligence');
    }
  };

  const fetchCases = async () => {
    try {
      const response = await fetch(`${API}/virtual-soc/incidents/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch cases');
      const data = await response.json();
      setCases(data.items || []);
    } catch (error) {
      console.error('Cases fetch error:', error);
      toast.error('Failed to load incident cases');
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${API}/virtual-soc/connectors/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch agents');
      const data = await response.json();
      setAgents(data.items || []);
    } catch (error) {
      console.error('Agents fetch error:', error);
      toast.error('Failed to load security agents');
    }
  };

  const fetchMitreData = async () => {
    try {
      const response = await fetch(`${API}/virtual-soc/mitre-attack/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch MITRE data');
      const data = await response.json();
      setMitreData(data);
    } catch (error) {
      console.error('MITRE data fetch error:', error);
      toast.error('Failed to load MITRE ATT&CK data');
    }
  };

  const executePlaybook = async (playbookId) => {
    try {
      const response = await fetch(`${API}/virtual-soc/playbooks/${playbookId}/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });
      if (!response.ok) throw new Error('Failed to execute playbook');
      const data = await response.json();
      toast.success(`Playbook execution initiated: ${data.execution_id}`);
      fetchPlaybooks(); // Refresh playbooks
    } catch (error) {
      console.error('Playbook execution error:', error);
      toast.error('Failed to execute playbook');
    }
  };

  const createNewCase = async () => {
    try {
      // Validate required fields
      if (!newCaseData.title.trim()) {
        toast.error('Case title is required');
        return;
      }
      if (!newCaseData.description.trim()) {
        toast.error('Case description is required');
        return;
      }
      if (!newCaseData.assigned_analyst.trim()) {
        toast.error('Assigned analyst is required');
        return;
      }

      const response = await fetch(`${API}/virtual-soc/incidents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCaseData)
      });
      
      if (!response.ok) throw new Error('Failed to create case');
      const data = await response.json();
      
      toast.success(`Incident case created successfully: ${data.case_id}`);
      setShowNewCaseModal(false);
      setNewCaseData({
        title: '',
        description: '',
        severity: 'medium',
        assigned_analyst: '',
        mitre_techniques: [],
        related_detections: [],
        iocs: [],
        impact: 'medium'
      });
      fetchCases(); // Refresh cases
    } catch (error) {
      console.error('Case creation error:', error);
      toast.error('Failed to create incident case');
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'red';
      case 'high': return 'orange';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      case 'info': return 'blue';
      default: return 'gray';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'high': return <ExclamationCircleIcon className="w-4 h-4" />;
      case 'medium': return <InformationCircleIcon className="w-4 h-4" />;
      case 'low': return <CheckCircleIcon className="w-4 h-4" />;
      default: return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const getSourceIcon = (source) => {
    switch (source?.toLowerCase()) {
      case 'siem': return <ShieldExclamationIcon className="w-4 h-4 text-blue-500" />;
      case 'soar': return <Cog6ToothIcon className="w-4 h-4 text-green-500" />;
      case 'threat_intel': return <GlobeAltIcon className="w-4 h-4 text-purple-500" />;
      case 'case_management': return <BugAntIcon className="w-4 h-4 text-yellow-500" />;
      // Legacy support for old names
      case 'wazuh': return <ShieldExclamationIcon className="w-4 h-4 text-blue-500" />;
      case 'cortex': return <Cog6ToothIcon className="w-4 h-4 text-green-500" />;
      case 'misp': return <GlobeAltIcon className="w-4 h-4 text-purple-500" />;
      case 'thehive': return <BugAntIcon className="w-4 h-4 text-yellow-500" />;
      default: return <ServerIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'new': return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
      case 'open': return <ExclamationCircleIcon className="w-4 h-4 text-orange-500" />;
      case 'in_progress': return <ClockIcon className="w-4 h-4 text-blue-500" />;
      case 'resolved': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      case 'closed': return <XCircleIcon className="w-4 h-4 text-gray-500" />;
      default: return <InformationCircleIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
          <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Loading threat response platform...</span>
        </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        {/* Page Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <ShieldExclamationIcon className="w-8 h-8 text-red-500 mr-3" />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Ai_STDR</h1>
                  <p className="text-sm text-gray-500">Security Threat Detection & Response</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    autoRefresh 
                      ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <ArrowPathIcon className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                  Auto Refresh
                </button>
                <button
                  onClick={() => setShowNewCaseModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  New Case
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex space-x-8">
              {[
                { key: 'dashboard', label: 'Unified Dashboard', icon: ChartBarIcon },
                { key: 'detections', label: 'Threat Detections', icon: ShieldExclamationIcon },
                { key: 'response', label: 'Automated Response', icon: BoltIcon },
                { key: 'intelligence', label: 'Threat Intel', icon: GlobeAltIcon },
                { key: 'cases', label: 'Incident Cases', icon: DocumentTextIcon },
                { key: 'mitre', label: 'MITRE ATT&CK', icon: ShieldCheckIcon }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && dashboardData && (
            <div className="space-y-6">
              {/* Platform Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Detections</p>
                      <p className="text-2xl font-bold text-gray-900">{dashboardData.total_events_24h}</p>
                      <p className="text-xs text-gray-500 mt-1">Critical: {dashboardData.critical_incidents}</p>
                    </div>
                    <ShieldExclamationIcon className="w-8 h-8 text-red-500" />
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Automated Responses</p>
                      <p className="text-2xl font-bold text-gray-900">{dashboardData.ai_correlation?.auto_escalated_incidents || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Playbooks: {Object.keys(dashboardData.connector_health || {}).length}</p>
                    </div>
                    <BoltIcon className="w-8 h-8 text-green-500" />
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Threat Intel Hits</p>
                      <p className="text-2xl font-bold text-gray-900">{dashboardData.ai_correlation?.total_correlations_24h || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">Active IOCs</p>
                    </div>
                    <GlobeAltIcon className="w-8 h-8 text-purple-500" />
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Active Cases</p>
                      <p className="text-2xl font-bold text-gray-900">{dashboardData.active_incidents}</p>
                      <p className="text-xs text-gray-500 mt-1">SLA: {Math.round(dashboardData.sla_compliance_rate || 0)}%</p>
                    </div>
                    <DocumentTextIcon className="w-8 h-8 text-yellow-500" />
                  </div>
                </motion.div>
              </div>

              {/* Platform Integration Status */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="bg-white rounded-xl shadow-md p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Platform Status</h3>
                  <div className="space-y-4">
                    {Object.entries(dashboardData.connector_health || {}).map(([platform, status]) => {
                      const platformLabels = {
                        'crowdstrike': 'CrowdStrike EDR',
                        'sentinel_one': 'SentinelOne EDR',
                        'splunk': 'Splunk SIEM',
                        'qradar': 'IBM QRadar',
                        'phantom': 'Phantom SOAR'
                      };
                      
                      const platformDescriptions = {
                        'crowdstrike': `${status.vendor} - ${status.events_ingested_24h} events/24h`,
                        'sentinel_one': `${status.vendor} - ${status.events_ingested_24h} events/24h`,
                        'splunk': `${status.vendor} - ${status.events_ingested_24h} events/24h`,
                        'qradar': `${status.vendor} - ${status.events_ingested_24h} events/24h`,
                        'phantom': `${status.vendor} - ${status.events_ingested_24h} events/24h`
                      };

                      return (
                        <div key={platform} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {getSourceIcon(platform)}
                            <div>
                              <p className="font-medium text-gray-900">{platformLabels[platform] || platform}</p>
                              <p className="text-sm text-gray-500">{platformDescriptions[platform] || ''}</p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                            status.api_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {status.api_available ? 'active' : 'offline'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.01 }}
                  className="bg-white rounded-xl shadow-md p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">MITRE ATT&CK Coverage</h3>
                  <div className="space-y-3">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{Math.round((dashboardData.ai_correlation?.correlation_accuracy || 0) * 100)}%</p>
                      <p className="text-sm text-gray-600">AI Accuracy</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(dashboardData.mitre_tactics_count || {}).slice(0, 4).map(([tactic, count]) => (
                        <div key={tactic} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-900 capitalize">{tactic.replace('-', ' ')}</p>
                          <p className="text-lg font-bold text-gray-700">{count}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Recent Detections */}
              <motion.div
                whileHover={{ scale: 1.01 }}
                className="bg-white rounded-xl shadow-md p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Threat Detections</h3>
                <div className="space-y-3">
                  {(dashboardData.recent_live_events || []).slice(0, 5).map((detection) => (
                    <div key={detection.event_id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-3">
                        {getSeverityIcon(detection.severity)}
                        <div>
                          <p className="font-medium text-gray-900">{detection.event_type}</p>
                          <p className="text-sm text-gray-500">
                            {detection.mitre_technique} • {detection.source_name} • {new Date(detection.timestamp).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-bold rounded-full bg-${getSeverityColor(detection.severity)}-100 text-${getSeverityColor(detection.severity)}-800`}>
                          {detection.severity}
                        </span>
                        {getStatusIcon(detection.status)}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          )}

          {/* Threat Detections Tab */}
          {activeTab === 'detections' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex flex-wrap gap-4">
                  <select
                    value={filters.severity}
                    onChange={(e) => setFilters({...filters, severity: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  
                  <select
                    value={filters.source}
                    onChange={(e) => setFilters({...filters, source: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Sources</option>
                    <option value="siem">SIEM & Log Analysis</option>
                    <option value="soar">Security Orchestration</option>
                    <option value="threat_intel">Threat Intelligence</option>
                    <option value="case_management">Case Management</option>
                  </select>
                  
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="new">New</option>
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>

                  <button
                    onClick={fetchDetections}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>

              {/* Detections Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {detections.map((detection) => (
                  <motion.div
                    key={detection.id}
                    whileHover={{ scale: 1.02 }}
                    className={`bg-white rounded-xl shadow-md p-6 border-l-4 border-${getSeverityColor(detection.severity)}-500 cursor-pointer`}
                    onClick={() => setSelectedDetection(detection)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        {getSeverityIcon(detection.severity)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full bg-${getSeverityColor(detection.severity)}-100 text-${getSeverityColor(detection.severity)}-800`}>
                          {detection.severity?.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {getSourceIcon(detection.source)}
                        {getStatusIcon(detection.status)}
                      </div>
                    </div>

                    <h3 className="font-bold text-gray-900 mb-2">{detection.title}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{detection.description}</p>

                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
                      <div>
                        <span className="font-medium">MITRE:</span> {detection.mitre_technique}
                      </div>
                      <div>
                        <span className="font-medium">Target:</span> {detection.target_asset}
                      </div>
                      <div>
                        <span className="font-medium">Confidence:</span> {detection.confidence}%
                      </div>
                      <div>
                        <span className="font-medium">Events:</span> {detection.event_count}
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="text-xs text-gray-500">
                        {new Date(detection.timestamp).toLocaleString()}
                      </div>
                      <div className="flex items-center gap-2">
                        {detection.response_triggered && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Response Triggered
                          </span>
                        )}
                        {detection.case_created && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Case Created
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Response Automation Tab */}
          {activeTab === 'response' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Response Playbooks</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {playbooks.map((playbook) => (
                    <motion.div
                      key={playbook.id}
                      whileHover={{ scale: 1.02 }}
                      className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-gray-900 mb-1">{playbook.name}</h4>
                          <p className="text-sm text-gray-600">{playbook.description}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                          playbook.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {playbook.status}
                        </span>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">MITRE Techniques:</span> {(playbook.mitre_techniques || []).join(', ')}
                        </div>
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Success Rate:</span> {playbook.success_rate}%
                        </div>
                        <div className="text-xs text-gray-500">
                          <span className="font-medium">Avg Execution:</span> {playbook.execution_time}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        {(playbook.actions || []).slice(0, 3).map((action) => (
                          <div key={action.step} className="flex items-center gap-2 text-sm">
                            {action.status === 'completed' && <CheckCircleIcon className="w-4 h-4 text-green-500" />}
                            {action.status === 'in_progress' && <ClockIcon className="w-4 h-4 text-blue-500" />}
                            {action.status === 'pending' && <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />}
                            <span className="text-gray-700">{action.action}</span>
                          </div>
                        ))}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => executePlaybook(playbook.id)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                        >
                          <PlayIcon className="w-3 h-3" />
                          Execute
                        </button>
                        <button
                          onClick={() => setShowPlaybookModal(playbook)}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          <EyeIcon className="w-3 h-3" />
                          View Details
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Threat Intelligence Tab */}
          {activeTab === 'intelligence' && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Threat Intelligence Indicators</h3>
                
                <DataTable
                  columns={[
                    {
                      id: "indicator",
                      header: "Indicator",
                      cell: ({ row }) => (
                        <div>
                          <p className="font-medium text-gray-900">{row.original.value}</p>
                          <p className="text-xs text-gray-500">{row.original.category}</p>
                        </div>
                      ),
                    },
                    {
                      accessorKey: "type",
                      header: "Type",
                      cell: ({ getValue }) => (
                        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{getValue()}</span>
                      ),
                    },
                    {
                      accessorKey: "confidence",
                      header: "Confidence",
                      cell: ({ getValue }) => (
                        <div className="flex items-center">
                          <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${getValue()}%` }}></div>
                          </div>
                          <span className="text-xs text-gray-600">{getValue()}%</span>
                        </div>
                      ),
                    },
                    {
                      accessorKey: "status",
                      header: "Status",
                      cell: ({ getValue }) => (
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                          getValue() === 'active' ? 'bg-green-100 text-green-800' :
                          getValue() === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {getValue()}
                        </span>
                      ),
                    },
                    {
                      id: "mitre",
                      header: "MITRE",
                      cell: ({ row }) => <span className="text-xs text-gray-600">{(row.original.mitre_techniques || []).join(', ')}</span>,
                    },
                    {
                      accessorKey: "hit_count",
                      header: "Hits",
                      cell: ({ getValue }) => (
                        <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">{getValue()}</span>
                      ),
                    },
                    {
                      accessorKey: "last_seen",
                      header: "Last Seen",
                      cell: ({ getValue }) => <span className="text-xs text-gray-500">{new Date(getValue()).toLocaleString()}</span>,
                    },
                  ]}
                  data={threatIntel}
                  getRowId={(indicator) => String(indicator.id)}
                  emptyMessage="No threat intelligence indicators found"
                  enableColumnVisibility={false}
                  enableColumnResizing
                  maxBodyHeight="65vh"
                />
              </div>
            </div>
          )}

          {/* Incident Cases Tab */}
          {activeTab === 'cases' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cases.map((incidentCase) => (
                  <motion.div
                    key={incidentCase.id}
                    whileHover={{ scale: 1.02 }}
                    className={`bg-white rounded-xl shadow-md p-6 border-l-4 border-${getSeverityColor(incidentCase.severity)}-500 cursor-pointer`}
                    onClick={() => setSelectedCase(incidentCase)}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">{incidentCase.title}</h3>
                        <p className="text-sm text-gray-600">{incidentCase.description}</p>
                      </div>
                      {getStatusIcon(incidentCase.status)}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
                      <div>
                        <span className="font-medium">Severity:</span> {incidentCase.severity}
                      </div>
                      <div>
                        <span className="font-medium">Status:</span> {incidentCase.status}
                      </div>
                      <div>
                        <span className="font-medium">Tasks:</span> {(incidentCase.tasks || []).length}
                      </div>
                      <div>
                        <span className="font-medium">Evidence:</span> {incidentCase.evidence_count}
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 mb-4">
                      <span className="font-medium">MITRE:</span> {(incidentCase.mitre_techniques || []).join(', ')}
                    </div>

                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Assigned:</span> {incidentCase.assigned_analyst}
                    </div>
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Created:</span> {new Date(incidentCase.created_at).toLocaleString()}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* MITRE ATT&CK Tab */}
          {activeTab === 'mitre' && mitreData && (
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">MITRE ATT&CK Framework Coverage</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{mitreData.coverage_summary.total_techniques_covered}</p>
                    <p className="text-sm text-gray-600">Techniques Covered</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{mitreData.coverage_summary.total_tactics_covered}</p>
                    <p className="text-sm text-gray-600">Tactics Covered</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{mitreData.coverage_summary.coverage_percentage}%</p>
                    <p className="text-sm text-gray-600">Coverage Percentage</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Kill Chain Coverage</h4>
                    <div className="space-y-2">
                      {mitreData.kill_chain_phases.map((phase) => (
                        <div key={phase.phase} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {phase.phase.replace('-', ' ')}
                          </span>
                          <span className="px-2 py-1 text-xs font-bold bg-blue-100 text-blue-800 rounded-full">
                            {phase.techniques} techniques
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Top Detected Techniques</h4>
                    <div className="space-y-2">
                      {mitreData.techniques.slice(0, 6).map((technique) => (
                        <div key={technique.technique} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{technique.technique}</p>
                            <p className="text-xs text-gray-500">{technique.name}</p>
                          </div>
                          <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded-full">
                            {technique.detections} detections
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Case Modal */}
      {showNewCaseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Incident Case</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Case Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCaseData.title}
                  onChange={(e) => setNewCaseData({...newCaseData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Suspicious Network Activity Investigation"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newCaseData.description}
                  onChange={(e) => setNewCaseData({...newCaseData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Provide detailed description of the security incident..."
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                  <select
                    value={newCaseData.severity}
                    onChange={(e) => setNewCaseData({...newCaseData, severity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                  <select
                    value={newCaseData.impact}
                    onChange={(e) => setNewCaseData({...newCaseData, impact: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Analyst <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={newCaseData.assigned_analyst}
                  onChange={(e) => setNewCaseData({...newCaseData, assigned_analyst: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="analyst@company.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MITRE ATT&CK Techniques</label>
                <input
                  type="text"
                  value={(newCaseData.mitre_techniques || []).join(', ')}
                  onChange={(e) => setNewCaseData({...newCaseData, mitre_techniques: e.target.value.split(',').map(t => t.trim())})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="T1110.001, T1059.001 (comma separated)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indicators of Compromise (IOCs)</label>
                <input
                  type="text"
                  value={newCaseData.iocs.join(', ')}
                  onChange={(e) => setNewCaseData({...newCaseData, iocs: e.target.value.split(',').map(i => i.trim())})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="IP addresses, domains, hashes (comma separated)"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowNewCaseModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createNewCase}
                disabled={!newCaseData.title.trim() || !newCaseData.description.trim() || !newCaseData.assigned_analyst.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Create Case
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default AutomatedThreatResponse;